import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faVideo, 
  faPlay,
  faRefresh,
  faSpinner,
  faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import { LibraryItem } from '../../../types';
import { Button } from '@mantine/core';

interface VideoLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoSelected: (item: LibraryItem) => void;
  projectId: string;
}

export const VideoLibraryModal: React.FC<VideoLibraryModalProps> = ({
  isOpen,
  onClose,
  onVideoSelected,
  projectId
}) => {
  const { t } = useTranslation('aiGenerator');
  const [videos, setVideos] = useState<LibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { showNotification } = useNotification();

  // Load videos when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadVideos();
    }
  }, [isOpen, projectId]);

  const loadVideos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl('')}/media/library/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load library: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🎬 Raw library response:', data); // Debug
      console.log('🎬 Data structure keys:', Object.keys(data)); // Debug
      
      // Handle different possible data structures
      const items = data.data || data.items || data || [];
      console.log('🎬 Items array:', items); // Debug
      console.log('🎬 Items length:', items.length); // Debug
      
      // Filter for videos only
      const videoItems = items.filter((item: LibraryItem) => {
        console.log('🎬 Checking item:', {
          name: item.name,
          type: item.type,
          url: item.url,
          id: item.id
        }); // Debug
        
        const isVideo = item.type === 'video' || 
               item.url?.toLowerCase().includes('.mp4') ||
               item.url?.toLowerCase().includes('.mov') ||
               item.url?.toLowerCase().includes('.avi') ||
               item.url?.toLowerCase().includes('.webm');
               
        console.log('🎬 Is video?', isVideo); // Debug
        return isVideo;
      });
      
      console.log('🎬 Final filtered videos:', videoItems); // Debug
      console.log('🎬 Video count:', videoItems.length); // Debug
      
      setVideos(videoItems);
      
      if (videoItems.length === 0) {
        console.log('🎬 No videos found after filtering'); // Debug
      }
      
    } catch (err: any) {
      console.error('🎬 Error loading videos:', err);
      setError(err.message || t('libraryModal.video.error', { error: 'Unknown' }));
      showNotification({
        title: t('common.error'),
        message: t('libraryModal.video.error', { error: err.message || 'Unknown' }),
        type: 'error'
      });
    } finally {
      console.log('🎬 Setting loading to false'); // Debug
      setIsLoading(false);
    }
  };

  const handleVideoSelect = (video: LibraryItem) => {
    onVideoSelected(video);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'hidden',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <h2 style={{ color: '#fff', margin: '0 0 4px 0' }}>
              <FontAwesomeIcon icon={faVideo} style={{ marginRight: '8px' }} />
              {t('libraryModal.video.title')}
            </h2>
            <p style={{ color: '#999', margin: '0', fontSize: '14px' }}>
              {t('libraryModal.video.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '8px'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {isLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999'
            }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', marginBottom: '12px' }} />
              <p>{t('libraryModal.video.loading')}</p>
            </div>
          )}

          {error && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#ef4444'
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '24px', marginBottom: '12px' }} />
              <p>{error}</p>
              <Button onClick={loadVideos} style={{ marginTop: '12px' }}>
                <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '8px' }} />
                {t('libraryModal.video.retry')}
              </Button>
            </div>
          )}

          {!isLoading && !error && videos.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999'
            }}>
              <FontAwesomeIcon icon={faVideo} style={{ fontSize: '48px', marginBottom: '16px' }} />
              <p>{t('libraryModal.video.noVideos')}</p>
              <p style={{ fontSize: '14px' }}>
                {t('libraryModal.video.noVideosDesc')}
              </p>
            </div>
          )}

          {!isLoading && !error && videos.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '16px'
            }}>
              {videos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => handleVideoSelect(video)}
                  style={{
                    border: '1px solid #333',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#222'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#8b5cf6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#333';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ position: 'relative', aspectRatio: '16/9' }}>
                    <video
                      src={video.url}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      muted
                      preload="metadata"
                    />
                    <div style={{
                      position: 'absolute',
                      inset: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.3)'
                    }}>
                      <FontAwesomeIcon icon={faPlay} style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    {video.duration && (
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '12px' }}>
                    <h4 style={{ color: '#fff', margin: '0 0 4px 0', fontSize: '14px' }}>
                      {video.name}
                    </h4>
                    <p style={{ color: '#999', margin: '0', fontSize: '12px' }}>
                      {video.source === 'ai-generated' ? t('libraryModal.video.aiGenerated') : t('libraryModal.video.uploaded')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '20px',
          textAlign: 'right',
          borderTop: '1px solid #333',
          paddingTop: '16px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {t('libraryModal.video.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};